import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import SaaSLayout, { THEME } from './SaasLayout';

// ICONS
import { 
    LayoutDashboard, Building2, FolderKanban, Map, BarChart3, Truck, 
    Users, ShieldAlert, Zap, FileSignature, GraduationCap, Megaphone, 
    FileCheck, Settings, LifeBuoy, UserCircle, LogOut, ClipboardList, 
    Briefcase
} from 'lucide-react';

// OWNER COMPONENTS
import OwnerOverview from './OwnerOverview';
import CompanyDirectory from './CompanyDirectory';
import CompanyProfile from './CompanyProfile';
import ProjectManager from './ProjectManager';
import DocumentReview from './DocumentReview';
import SafetyManager from './SafetyManager'; 
import PermitManager from './PermitManager';
import UserManagement from './UserManagement';
import Inductions from './Inductions';
import ToolboxTalks from './ToolboxTalks';
import Analytics from './Analytics';
import SupportTickets from './SupportTickets';
import DailyLogBuilder from './DailyLogBuilder';
import SiteMap from './SiteMap';
import CorrectiveActionManager from './CorrectiveActionManager';
import EquipmentManager from './EquipmentManager';
import PeopleManager from './PeopleManager';
import MyProfile from './MyProfile';

// CONTRACTOR COMPONENTS
import MyCompany from './MyCompany';
import ContractorLogManager from './ContractorLogManager';
import ContractorPermitList from './ContractorPermitList';

const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(false);
    const [username, setUsername] = useState('');
    
    // Navigation State
    const [activeView, setActiveView] = useState('overview'); 
    const [selectedCompany, setSelectedCompany] = useState(null); 

    // LINKING STATE
    const [targetId, setTargetId] = useState(null); 
    const [targetTab, setTargetTab] = useState(null);

    useEffect(() => {
        const checkRole = async () => {
            try {
                const res = await api.get('users/me/');
                setUsername(res.data.username);
                if (res.data.is_superuser || res.data.is_staff) {
                    setIsOwner(true);
                    setActiveView('overview');
                } else {
                    setIsOwner(false);
                    setActiveView('my_logs'); 
                }
            } catch (error) {
                console.error("Role check failed:", error);
                navigate('/'); 
            } finally {
                setLoading(false);
            }
        };
        checkRole();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('username');
        navigate('/');
    };

    if (loading) return <div style={{background: '#0f172a', height: '100vh', color: 'white', display:'flex', alignItems:'center', justifyContent:'center'}}>Loading SafetyOS...</div>;

    const handleNavigate = (view, id = null) => {
        if (view === 'audits') {
            setActiveView('safety');
            setTargetTab('audits');
            setTargetId(id);
        } else if (view === 'safety') {
            setActiveView('safety');
            setTargetTab('incidents');
            setTargetId(id);
        } else if (view === 'permits') {
            setActiveView('permits');
            setTargetId(id); 
            setTargetTab(null);
        } else {
            setActiveView(view);
            setTargetTab(null);
            setTargetId(null);
        }
    };

    // --- SIDEBARS WITH ICONS ---
    const OwnerSidebar = (
        <>
            <NavItem label="Overview" icon={LayoutDashboard} active={activeView === 'overview'} onClick={() => { setActiveView('overview'); setSelectedCompany(null); }} />
            
            <NavSection title="Site Management" />
            <NavItem label="Projects" icon={FolderKanban} active={activeView === 'projects'} onClick={() => setActiveView('projects')} />
            <NavItem label="Site Map" icon={Map} active={activeView === 'map'} onClick={() => setActiveView('map')} />
            <NavItem label="Analytics" icon={BarChart3} active={activeView === 'analytics'} onClick={() => setActiveView('analytics')} />
            <NavItem label="Equipment" icon={Truck} active={activeView === 'equipment'} onClick={() => handleNavigate('equipment')} />
            <NavItem label="People & Certs" icon={Users} active={activeView === 'people'} onClick={() => handleNavigate('people')} />

            <NavSection title="Safety & Quality" />
            <NavItem label="Safety Hub" icon={ShieldAlert} active={activeView === 'safety'} onClick={() => handleNavigate('safety')} />
            <NavItem label="Corrective Actions" icon={Zap} active={activeView === 'actions'} onClick={() => handleNavigate('actions')} />
            <NavItem label="Permits" icon={FileSignature} active={activeView === 'permits'} onClick={() => handleNavigate('permits')} />
            <NavItem label="Inductions" icon={GraduationCap} active={activeView === 'inductions'} onClick={() => handleNavigate('inductions')} />
            <NavItem label="Toolbox Talks" icon={Megaphone} active={activeView === 'toolbox'} onClick={() => handleNavigate('toolbox')} />
            
            <NavSection title="Contractors" />
            <NavItem label="Database" icon={Building2} active={activeView === 'companies'} onClick={() => handleNavigate('companies')} />
            <NavItem label="Compliance" icon={FileCheck} active={activeView === 'compliance'} onClick={() => handleNavigate('compliance')} />
            <NavItem label="Log Config" icon={Settings} active={activeView === 'log_builder'} onClick={() => handleNavigate('log_builder')} />
            
            <NavSection title="System" />
            <NavItem label="User Accounts" icon={Users} active={activeView === 'users'} onClick={() => handleNavigate('users')} />
            <NavItem label="Support" icon={LifeBuoy} active={activeView === 'support'} onClick={() => handleNavigate('support')} />
            <NavItem label="My Profile" icon={UserCircle} active={activeView === 'profile'} onClick={() => handleNavigate('profile')} />
            
            <div style={{marginTop:'auto', paddingTop:'20px', borderTop:`1px solid ${THEME.border}`}}>
                <NavItem label="Logout" icon={LogOut} onClick={handleLogout} />
            </div>
        </>
    );

    const ContractorSidebar = (
        <>
            <NavSection title="My Portal" />
            <NavItem label="Daily Logs" icon={ClipboardList} active={activeView === 'my_logs'} onClick={() => handleNavigate('my_logs')} />
            <NavItem label="Company Profile" icon={Briefcase} active={activeView === 'my_company'} onClick={() => handleNavigate('my_company')} />
            <NavItem label="My Permits" icon={FileSignature} active={activeView === 'my_permits'} onClick={() => handleNavigate('my_permits')} />
            
            <NavSection title="Site Actions" />
            <NavItem label="Safety Report" icon={ShieldAlert} active={activeView === 'safety'} onClick={() => handleNavigate('safety')} />
            <NavItem label="My Actions" icon={Zap} active={activeView === 'actions'} onClick={() => handleNavigate('actions')} />
            <NavItem label="Inductions" icon={GraduationCap} active={activeView === 'inductions'} onClick={() => handleNavigate('inductions')} />
            <NavItem label="Toolbox Talks" icon={Megaphone} active={activeView === 'toolbox'} onClick={() => handleNavigate('toolbox')} />
            <NavItem label="Support" icon={LifeBuoy} active={activeView === 'support'} onClick={() => handleNavigate('support')} />
            <NavItem label="Site Map" icon={Map} active={activeView === 'map'} onClick={() => handleNavigate('map')} />
            <NavItem label="My Profile" icon={UserCircle} active={activeView === 'profile'} onClick={() => handleNavigate('profile')} />
            
            <div style={{marginTop:'auto', paddingTop:'20px', borderTop:`1px solid ${THEME.border}`}}>
                <NavItem label="Logout" icon={LogOut} onClick={handleLogout} />
            </div>
        </>
    );

    return (
        <SaaSLayout title={getViewTitle(activeView, isOwner)} sidebar={isOwner ? OwnerSidebar : ContractorSidebar} onNavigate={handleNavigate}>
            {isOwner ? (
                <>
                    {activeView === 'overview' && <OwnerOverview onNavigate={handleNavigate} currentUser={username} />}
                    {activeView === 'companies' && (selectedCompany ? <CompanyProfile company={selectedCompany} onBack={() => setSelectedCompany(null)} /> : <CompanyDirectory onSelectCompany={(c) => setSelectedCompany(c)} /> )}
                    {activeView === 'projects' && <ProjectManager />}
                    {activeView === 'map' && <SiteMap />}
                    {activeView === 'analytics' && <Analytics />}
                    {activeView === 'people' && <PeopleManager />}
                    {activeView === 'safety' && <SafetyManager initialTab={targetTab || 'incidents'} initialSelectedId={targetId} />}
                    {activeView === 'actions' && <CorrectiveActionManager onNavigate={handleNavigate} />}
                    {activeView === 'permits' && <PermitManager initialSelectedId={targetId} />}
                    {activeView === 'inductions' && <Inductions />}
                    {activeView === 'toolbox' && <ToolboxTalks />}
                    {activeView === 'compliance' && <DocumentReview />}
                    {activeView === 'equipment' && <EquipmentManager />}
                    {activeView === 'log_builder' && <DailyLogBuilder />}
                    {activeView === 'users' && <UserManagement />}
                    {activeView === 'support' && <SupportTickets isStaff={true} />}
                    {activeView === 'profile' && <MyProfile onNavigate={handleNavigate}/>}
                </>
            ) : (
                <>
                    {activeView === 'my_logs' && <ContractorLogManager />}
                    {activeView === 'my_company' && <MyCompany />}
                    {activeView === 'my_permits' && <ContractorPermitList />}
                    {activeView === 'safety' && <SafetyManager initialTab={targetTab || 'incidents'} initialSelectedId={targetId} />}
                    {activeView === 'actions' && <CorrectiveActionManager onNavigate={handleNavigate} />}
                    {activeView === 'inductions' && <Inductions />}
                    {activeView === 'toolbox' && <ToolboxTalks />}
                    {activeView === 'support' && <SupportTickets isStaff={false} />}
                    {activeView === 'map' && <SiteMap />}
                    {activeView === 'profile' && <MyProfile onNavigate={handleNavigate}/>}
                </>
            )}
        </SaaSLayout>
    );
};

const NavSection = ({ title }) => (
    <div style={{ margin: '20px 0 8px 15px', fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {title}
    </div>
);

// Updated NavItem to handle the Icon Component
const NavItem = ({ label, icon: Icon, active, onClick }) => (
    <button 
        onClick={onClick} 
        style={{
            display: 'flex', alignItems: 'center', gap: '12px', width: '100%', 
            padding: '10px 14px', border: 'none', borderRadius: '8px', 
            background: active ? 'rgba(59, 130, 246, 0.1)' : 'transparent', 
            color: active ? '#60a5fa' : '#94a3b8', 
            cursor: 'pointer', fontSize: '0.9rem', fontWeight: active ? 600 : 500, 
            textAlign: 'left', transition: 'all 0.2s ease',
            marginBottom: '2px'
        }}
        onMouseEnter={(e) => {
            if(!active) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                e.currentTarget.style.color = '#e2e8f0';
            }
        }}
        onMouseLeave={(e) => {
            if(!active) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
            }
        }}
    >
        <Icon size={18} strokeWidth={active ? 2.5 : 2} />
        {label}
    </button>
);

const getViewTitle = (view, isOwner) => {
    const titles = { 
        overview: "Site Overview", 
        projects: "Project Management", 
        map: "Live Site Map", 
        analytics: "Analytics Dashboard", 
        safety: "Safety & Incident Hub", 
        actions: "Corrective Action Manager", 
        permits: "Permit to Work Control", 
        inductions: "Induction Registry", 
        toolbox: "Toolbox Talks", 
        companies: "Contractor Database", 
        compliance: "Document Compliance", 
        log_builder: "Daily Log Configuration", 
        users: "User Management", 
        support: "Support Tickets", 
        equipment: "Equipment Register",
        people: "People & Qualifications",
        profile: "My Profile",
        my_company: "Company Profile", 
        my_logs: "Daily Work Logs", 
        my_permits: "My Permits" 
    };
    return titles[view] || "Dashboard";
};

export default Dashboard;